import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { toast } from "react-toastify";
import { fetchCourierShippingOptions, createCourierShippingOption, updateCourierShippingOption, deleteCourierShippingOption } from "./courierShippingOptionsAsyncThunk";
import { DeleteCourier } from "../../../types/CourierTypes";
import { CourierShippingOption } from "../../../types/CourierShippingOptionsTypes";
import { RequestResults } from "../../common/RequestStatus";

interface CourierShippingOptionsState {
  courierShippingOptions: CourierShippingOption[];
  status: RequestResults;
  loading: boolean;
  error: string | null;
}

const initialState: CourierShippingOptionsState = {
  courierShippingOptions: [],
  status: RequestResults.idle,
  loading: false,
  error: null,
};

// Slice
const courierShippingOptionsSlice = createSlice({
  name: "courierShippingOptions",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Fetch Courier Shipping Options
    builder
      .addCase(fetchCourierShippingOptions.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = RequestResults.pending;
      })
      .addCase(fetchCourierShippingOptions.fulfilled, (state, action: PayloadAction<CourierShippingOption[]>) => {
        state.courierShippingOptions = [...action.payload];
        state.loading = false;
        state.status = RequestResults.success;
      })
      .addCase(fetchCourierShippingOptions.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to fetch courier shipping options");
        state.error = action.payload || "Failed to fetch courier shipping options";
        state.status = RequestResults.failed;
      });

    // Add Courier
    builder
      .addCase(createCourierShippingOption.pending, (state) => {
        state.loading = true;
      })
      .addCase(createCourierShippingOption.fulfilled, (state, action: PayloadAction<CourierShippingOption>) => {
        state.loading = false;
        state.courierShippingOptions.push(action.payload);
      })
      .addCase(createCourierShippingOption.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to add courier shipping option");
        state.error = action.payload || "Failed to add courier shipping option";
      });

    // Update Courier
    builder
      .addCase(updateCourierShippingOption.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCourierShippingOption.fulfilled, (state, action: PayloadAction<CourierShippingOption>) => {
        const index = state.courierShippingOptions.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.courierShippingOptions[index] = action.payload;
        }
        state.loading = false;
      })
      .addCase(updateCourierShippingOption.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to update courier shipping option");
        state.error = action.payload || "Failed to update courier shipping option";
      });

    // Delete Courier
    builder
      .addCase(deleteCourierShippingOption.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteCourierShippingOption.fulfilled, (state, action: PayloadAction<DeleteCourier>) => {
        const index = state.courierShippingOptions.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.courierShippingOptions = state.courierShippingOptions.filter((item) => item.id !== action.payload.id);
        }
        state.loading = false;
      })
      .addCase(deleteCourierShippingOption.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to delete courier shipping option");
        state.error = action.payload || "Failed to delete courier shipping option";
      });
  },
});

export const selectCourierShippingOptions = (state: RootState) => state.courierShippingOptions;

export default courierShippingOptionsSlice.reducer;
